import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VoiceSearchButton from "./VoiceSearchButton";
import useSearchSuggestions from "../hooks/useSearchSuggestions";
import SearchSuggestDropdown from "./SearchSuggestDropdown";

/**
 * Meilisearch-style default ranking: when the visitor hasn't asked for a
 * specific sort, still don't show results in arbitrary API order — rank
 * verified businesses first, then by rating, then by review volume. Any
 * explicit sort ("Newest") or the "Top Rated" toggle overrides this.
 */
function byRelevance(a, b) {
    const va = Number(a.is_verified) === 1 ? 1 : 0;
    const vb = Number(b.is_verified) === 1 ? 1 : 0;
    if (vb !== va) return vb - va;
    const ra = Number(a.rating) || 0;
    const rb = Number(b.rating) || 0;
    if (rb !== ra) return rb - ra;
    return (Number(b.reviews) || 0) - (Number(a.reviews) || 0);
}

export default function FilterBar({ listings, onFilter }) {
    const navigate = useNavigate();
    const location = useLocation();

    // Seed the box from whatever's already in the URL (e.g. arriving here
    // from the home-page hero search), so it doesn't silently show empty
    // while the page heading still says "Best <original term>".
    const [search, setSearch] = useState(() => new URLSearchParams(location.search).get("search") || "");
    const [province, setProvince] = useState("");
    const [category, setCategory] = useState("");
    const [rating, setRating] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [topRated, setTopRated] = useState(false);

    const provinceOptions = [...new Set((listings || []).map(b => b.province).filter(Boolean))];
    const categoryOptions = [...new Set((listings || []).map(b => b.catagory).filter(Boolean))];

    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    // ---------- Make typing here an ACTUAL search, not just a client-side
    // filter of whatever narrow set was already loaded ----------
    // Bug this fixes: previously this box only filtered the `listings` array
    // already fetched for the *original* URL search term — so typing a brand
    // new query (e.g. switching from "Silicon Care" to "Vaibhav Tech
    // Plumber") never re-hit the backend and always showed "0 results",
    // even though matching businesses exist. Now, once the term settles,
    // we push it into the URL's `search` param — Listings.jsx's fetch effect
    // is keyed off that param and re-queries /api/searchListings.php across
    // ALL businesses, exactly like the home-page hero search does.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const currentUrlSearch = params.get("search") || "";
        if (debouncedSearch === currentUrlSearch) return;
        params.set("search", debouncedSearch);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // ---------- Global search suggestions (same hook/dropdown as HeroSection) ----------
    const searchBoxRef = useRef(null);
    const { suggestions, flatItems, loading: suggestLoading } = useSearchSuggestions(search);
    const [showSuggest, setShowSuggest] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

  const suppressReopenRef = useRef(false);

useEffect(() => {
    if (suppressReopenRef.current) {
        suppressReopenRef.current = false;
        return;
    }
    if (search.trim().length < 2) { setShowSuggest(false); return; }
    setShowSuggest(true);
    setActiveIndex(-1);
}, [search, suggestions]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggest(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const onSearchKeyDown = (e) => {
        if (!showSuggest || flatItems.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => (i + 1) % flatItems.length); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => (i <= 0 ? flatItems.length - 1 : i - 1)); }
        else if (e.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < flatItems.length) {
                e.preventDefault();
                const item = flatItems[activeIndex];
                if (item.kind === "category") handleSelectCategory(item.data);
                else handleSelectBusiness(item.data);
            }
        } else if (e.key === "Escape") { setShowSuggest(false); setActiveIndex(-1); }
    };

    // Selecting a category suggestion re-searches the backend for that
    // category (same fix as above — narrowing only the already-loaded
    // client list would show "0 results" if the current list came from an
    // unrelated original search term).
   const handleSelectCategory = (cat) => {
    suppressReopenRef.current = true;
    setShowSuggest(false); setActiveIndex(-1);
    setSearch(""); setCategory(cat.label);
    const params = new URLSearchParams(location.search);
    params.set("search", "");
    params.set("category", cat.label);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
};

const handleSelectBusiness = (biz) => {
    suppressReopenRef.current = true;
    setShowSuggest(false); setActiveIndex(-1);
    setSearch(biz.title);
};

    useEffect(() => {
        let filtered = listings || [];

        // NOTE: no client-side "does title include debouncedSearch" filter
        // here anymore — the backend (/api/searchListings.php) already
        // re-queries using this exact term via the URL-sync effect above,
        // and it may legitimately match on fields other than title (address,
        // keywords, etc). Re-filtering on title only would hide those.
        if (province)
            filtered = filtered.filter(b => b.province === province);
        if (category)
            filtered = filtered.filter(b => b.catagory === category);
        if (verifiedOnly)
            filtered = filtered.filter(b => Number(b.is_verified) === 1);
        if (rating)
            filtered = filtered.filter(b => Math.floor(b.rating) >= parseInt(rating));

        if (topRated || sortBy === "top-rated")
            filtered = [...filtered].sort((a, b) => b.rating - a.rating);
        else if (sortBy === "newest")
            filtered = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        else
            // No explicit sort chosen — default to relevance ranking
            // (verified > rating > review count), not raw API order.
            filtered = [...filtered].sort(byRelevance);

        if (typeof onFilter === "function") onFilter(filtered);
    }, [province, category, rating, sortBy, verifiedOnly, topRated, listings]);

    const resetFilters = () => {
        setSearch(""); setProvince(""); setCategory(""); setRating(""); setSortBy("");
        setVerifiedOnly(false); setTopRated(false);
        const params = new URLSearchParams(location.search);
        params.set("search", "");
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        if (typeof onFilter === "function") onFilter([...(listings || [])].sort(byRelevance));
    };

    // keep the on-scroll shadow like before
    useEffect(() => {
        const el = document.getElementById("filter-bar");
        if (!el) return;
        const onScroll = () => {
            el.style.boxShadow = window.scrollY > 120 ? "0 6px 18px rgba(0,0,0,0.10)" : "none";
        };
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div id="filter-bar" className="jd-filter-wrap">
            <style>{`
               .jd-filter-wrap { position: sticky; top: 90px; z-index: 1000; background: #fff; }

                .jd-search-row { padding: 10px 4px 6px; }
                .jd-search {
                    display: flex; align-items: center; gap: 6px; background: #fff;
                    border: 1px solid #d6dbe1; border-radius: 20px; padding: 5px 12px;
                    max-width: 420px;
                }
                .jd-search i { color: #8a9099; font-size: 13px; }
                .jd-search input { border: 0; outline: 0; font-size: 13px; flex: 1 1 auto; min-width: 0; background: transparent; }

                               .jd-filter-bar {
                    display: flex; align-items: center; gap: 7px;
                    padding: 8px 4px; overflow-x: auto; border-bottom: 1px solid #eef0f2;
                }
                .jd-filter-bar::-webkit-scrollbar { height: 0; }
                .jd-chip {
                    display: flex; align-items: center; gap: 5px; white-space: nowrap;
                    background: #fff; border: 1px solid #d6dbe1; border-radius: 20px;
                    padding: 5px 12px; font-size: 13px; color: #444; cursor: pointer; flex: 0 0 auto;
                    line-height: 1.2;
                }
                .jd-chip i { font-size: 13px; }
                .jd-chip:hover { border-color: #2563eb; }
                .jd-chip.active { background: #eef3ff; border-color: #2563eb; color: #2563eb; }
                .jd-chip i.bi-star-fill { color: #f5a623; }
                .jd-chip-select { position: relative; padding: 0; }
                .jd-chip-select select {
                    appearance: none; -webkit-appearance: none; border: 0; background: transparent;
                    padding: 5px 26px 5px 12px; font-size: 13px; color: #444; cursor: pointer; border-radius: 20px;
                }
                .jd-chip-select i { position: absolute; right: 10px; pointer-events: none; color: #8a9099; font-size: 11px; }
                .jd-reset { color: #dc3545; }

            `}</style>

            {/* Search — with the same global search-suggestions dropdown as the home page.
                Kept out of the horizontally-scrolling chip row below so the dropdown
                never gets clipped by that row's overflow-x. */}
            <div className="jd-search-row">
                <div className="jd-search position-relative" ref={searchBoxRef}>
                    <i className="bi bi-search"></i>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onFocus={() => { if (flatItems.length > 0 && search.trim().length >= 2) setShowSuggest(true); }}
                        onKeyDown={onSearchKeyDown}
                        placeholder="Search by name..."
                        autoComplete="off"
                    />
                    <VoiceSearchButton onResult={(text) => setSearch(text)} />

                    {showSuggest && search.trim().length >= 2 && (
                        <SearchSuggestDropdown
                            suggestions={suggestions}
                            flatItems={flatItems}
                            loading={suggestLoading}
                            activeIndex={activeIndex}
                            onHoverIndex={setActiveIndex}
                            onSelectCategory={handleSelectCategory}
                            onSelectBusiness={handleSelectBusiness}
                        />
                    )}
                </div>
            </div>

            <div className="jd-filter-bar">
                {/* Sort by */}
                <div className="jd-chip jd-chip-select">
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value="">Sort by</option>
                        <option value="newest">Newest</option>
                        <option value="top-rated">Top Rated</option>
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Top Rated toggle */}
                <button className={`jd-chip ${topRated ? "active" : ""}`} onClick={() => setTopRated(v => !v)}>
                    <i className="bi bi-star-fill"></i> Top Rated
                </button>

                {/* Verified toggle */}
                <button className={`jd-chip ${verifiedOnly ? "active" : ""}`} onClick={() => setVerifiedOnly(v => !v)}>
                    <i className="bi bi-patch-check-fill"></i> Verified
                </button>

                {/* Province */}
                <div className="jd-chip jd-chip-select">
                    <select value={province} onChange={e => setProvince(e.target.value)}>
                        <option value="">All Provinces</option>
                        {provinceOptions.map((p, i) => <option key={i}>{p}</option>)}
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Category */}
                <div className="jd-chip jd-chip-select">
                    <select value={category} onChange={e => setCategory(e.target.value)}>
                        <option value="">All Categories</option>
                        {categoryOptions.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Rating */}
                <div className="jd-chip jd-chip-select">
                    <select value={rating} onChange={e => setRating(e.target.value)}>
                        <option value="">Any Rating</option>
                        <option value="4">4★ &amp; up</option>
                        <option value="3">3★ & up</option>
                        <option value="2">2★ &amp; up</option>
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Reset */}
                <button className="jd-chip jd-reset" onClick={resetFilters}>
                    <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
            </div>
        </div>
    );
}